import { Stack, Typography } from "@mui/material";
import { useMemo } from "react";
import { SmsMessageWithSender } from "src/api/types";
import { useClinician } from "src/context/SessionProvider/ClinicianProvider";
import { parseMessage } from "src/pages/Cancellations/utils/parseMessage";
import { format } from "date-fns";
import MediaView from "../MediaView";

type Properties = {
  showName: boolean;
  backgroundColor: string;
  position: React.CSSProperties["alignItems"];
  color: string;
  textColor: string;
  textAlign: React.CSSProperties["textAlign"];
};

interface Props {
  textMessage: SmsMessageWithSender;
}

const TextMessage = ({ textMessage }: Props) => {
  const profileId = useClinician((clinician) => clinician.id);

  const properties: Properties = useMemo(() => {
    if (textMessage.sender.id === profileId) {
      return {
        showName: false,
        backgroundColor: "#00B6B9",
        position: "flex-end",
        color: "#91E7E8",
        textColor: "#fff",
        textAlign: "right",
      };
    }

    return {
      showName: true,
      backgroundColor: "#CCF6F7",
      color: "#82BEC0",
      position: "flex-start",
      textColor: "#000",
      textAlign: "left",
    };
  }, [textMessage, profileId]);

  const name = useMemo(() => {
    if (textMessage.sender.preferred_name) {
      return textMessage.sender.preferred_name;
    }
    return textMessage.sender.first_name + " " + textMessage.sender.last_name;
  }, [
    textMessage.sender.first_name,
    textMessage.sender.last_name,
    textMessage.sender.preferred_name,
  ]);

  const text = useMemo(() => {
    if (textMessage.body.trim().length > 0) {
      return parseMessage(textMessage.body);
    }
    return null;
  }, [textMessage.body]);

  return (
    <Stack role="listitem" sx={{ whiteSpace: "pre-wrap" }}>
      {properties.showName ? (
        <Typography color={properties.color} ml={1} variant="body2">
          {name}
        </Typography>
      ) : null}
      <Stack
        alignSelf={properties.position}
        p={2}
        gap={0.5}
        sx={{
          backgroundColor: properties.backgroundColor,
          borderRadius: "10px",
          minWidth: "10%",
          maxWidth: "60%",
          mb: 1,
          wordWrap: "break-word",
        }}
      >
        {textMessage.media?.map((media) => (
          <MediaView key={media.external_id} media={media} />
        ))}
        {text ? (
          <Typography
            role="article"
            aria-label="Message text"
            color={properties.textColor}
            textAlign={properties.textAlign}
          >
            {text}
          </Typography>
        ) : null}
        <Typography
          color={properties.color}
          textAlign={properties.textAlign}
          fontSize="0.7rem"
        >
          {format(new Date(textMessage.created_at), `MMM d yyyy 'at' h:mm a`)}
        </Typography>
      </Stack>
    </Stack>
  );
};

export default TextMessage;
